














































































import functions from '@/mixins/functions';
import { IItem, IItemData } from '@/models';
import { getPropertyFromItem } from '@/utils/helpers';
import mixins from '@/utils/mixins';
import { cloneDeep, startCase } from 'lodash';
import { PropType } from 'vue';

export default mixins(functions).extend({
  name: 'CsiItemSearch',
  props: {
    value: [Number, String, Array] as PropType<number | string | string[]>,
    text: [Number, String] as PropType<number | string>,
    noTooltip: Boolean,
    allowCopy: {
      type: Boolean,
      default: false
    },
    search: String,
    clearOnSelect: Boolean,
    clearOnSearchOnSelect: Boolean,
    placeholder: {
      type: String,
      default: 'Start typing to search item'
    },
    snapshot: {
      type: Number as PropType<number>
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    descriptionLimit: 60,
    entries: [] as IItemData[],
    cachedEntries: [] as IItemData[],
    isLoading: false,
    copyText: '' as string,

    showTooltip: false,
    activeTimeout: -1
  }),

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(value: IItemData | IItemData[]) {
        this.setValue(value);
      }
    },

    items(): IItemData[] {
      return this.filterDuplicates(this.cachedEntries.concat(this.entries)).map(
        (entry: any) => {
          if (entry) {
            entry.display_name =
              entry.item +
              ' - ' +
              entry.description.slice(0, this.descriptionLimit);
          }
          return entry;
        }
      );
    },

    selectedAllItems(): boolean {
      return (
        Array.isArray(this.internalValue) &&
        this.internalValue.length === this.items.length
      );
    },

    selectedSomeItems(): boolean {
      return (
        Array.isArray(this.internalValue) &&
        this.internalValue.length > 0 &&
        !this.selectedAllItems
      );
    },

    icon() {
      if (this.selectedAllItems) return 'mdi-close-box';
      if (this.selectedSomeItems) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    }
  },

  watch: {
    showTooltip: 'setTooltipTimeout',
    isLoading(value: boolean) {
      this.$emit('isLoading', value);
    }
  },

  mounted() {
    if (this.internalValue) {
      this.isLoading = true;

      if (this.multiple) {
        ((this.internalValue ?? []) as string[]).forEach(item => {
          this.$axios
            .get(`/v1/csi/items/${item}/`, {
              timeout: 5 * (60 * 1000),
              params: {
                pricing: this.snapshot
              }
            })
            .then(({ data }) => {
              this.entries.push(data);
              this.cachedEntries.push(data);

              this.$forceUpdate();
            })
            .finally(() => {
              this.isLoading = false;
            });
        });
      } else {
        this.$axios
          .get(`/v1/csi/items/${this.internalValue}/`, {
            timeout: 5 * (60 * 1000),
            params: {
              pricing: this.snapshot
            }
          })
          .then(({ data }) => {
            this.setCopyText(data);
            this.entries.push(data);
            this.cachedEntries.push(data);

            this.$forceUpdate();
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    }
    if (this.showTooltip) this.setTooltipTimeout();
  },

  methods: {
    async copyToClipBoard() {
      if (!navigator.clipboard) {
        return;
      }

      try {
        if (this.copyText) {
          await navigator.clipboard.writeText(this.copyText);
          this.showTooltip = true;
        }
      } catch (error) {
        console.error('copy failed', error);
      }
    },

    setTooltipTimeout() {
      window.clearTimeout(this.activeTimeout);

      const timeout = Number(4000);

      if (
        !this.showTooltip ||
        // TODO: remove 0 in v3
        [0, -1].includes(timeout)
      ) {
        return;
      }
      this.activeTimeout = window.setTimeout(() => {
        this.showTooltip = false;
      }, timeout);
    },

    setCopyText(val: any) {
      this.copyText = String(
        val.item + ' - ' + val.description.slice(0, this.descriptionLimit)
      );
    },

    async debounceSetData(search?: string) {
      this.isLoading = true;
      await this.debounce(this.setData, 500)(search);
    },

    setData(search?: string) {
      if (search) {
        this.fetchDataFromApi(search).then((data: any) => {
          this.entries = data.results;
        });
      }
      this.isLoading = false;
    },

    async fetchDataFromApi(search?: string) {
      // eslint-disable-next-line no-async-promise-executor
      return await new Promise(async (resolve, reject) => {
        // eslint-disable-next-line prefer-const
        if (!search) {
          resolve({
            results: [],
            count: 0
          });
        }
        const { results, count } = await this.getCsiItems(search ?? '');

        resolve({
          results,
          count
        });
      });
    },

    async getCsiItems(search?: string) {
      const params = new URLSearchParams();
      if (this.snapshot) {
        params.append('pricing', String(this.snapshot));
      }
      if (search!.length) {
        params.append('search', search!);
      } else {
        return { results: [], count: null };
      }

      return this.searchCsiItems(params);
    },

    searchCsiItems(params: URLSearchParams) {
      return this.$axios
        .get(`/v1/csi/items/`, {
          timeout: 5 * (60 * 1000),
          params
        })
        .then(({ data }: { data: { results: any[]; count: number } }) => data);
    },

    async setValue(val: IItemData | IItemData[]) {
      if (this.multiple) {
        if (Array.isArray(val)) {
          const items: string[] = cloneDeep(val).map((item: IItemData) =>
            this.getValue(item)
          );

          this.$emit('input', items);
          this.$emit('change', items);
          this.$emit('select', items);
        }
      } else {
        if (val) {
          this.setCopyText(val);
        }
        if (!Array.isArray(val)) {
          this.$emit(
            'update:text',
            val
              ? String(
                  val.item +
                    ' - ' +
                    val.description.slice(0, this.descriptionLimit)
                )
              : ''
          );
          this.$emit('input', val ? val.item : null);
          this.$emit('change', val ? val.item : null);
          this.$emit('select', val);
        }
      }
    },

    getValue(item: IItemData) {
      return getPropertyFromItem(item, 'item', item);
    },

    /**
     * removing filter from the csi search as to get the raw data from the server as is
     */
    itemSearch(item: any, queryText: string, itemText: string) {
      return true;
    },

    async fetchFirstPageCsiItems() {
      const params = new URLSearchParams();
      params.append('page', '1');
      if (this.snapshot) {
        params.append('pricing', String(this.snapshot));
      }
      const { results } = await this.searchCsiItems(params);

      this.entries = results;
    },

    async onSelect(item: IItemData | IItemData[]) {
      let cachedItem = item;
      if (this.multiple && Array.isArray(cachedItem)) {
        const lastItem = cachedItem[cachedItem.length - 1];
        cachedItem = lastItem;
      }
      if (cachedItem && !Array.isArray(cachedItem) && cachedItem.item) {
        this.cachedEntries.push(cachedItem);
      }

      if (this.clearOnSearchOnSelect) {
        (this.$refs.autocomplete as any).internalSearch = null;
      }
      if (this.clearOnSelect) {
        (this.$refs.autocomplete as any).clearableCallback();
      }
    },

    filterDuplicates(arr: IItemData[]) {
      const uniqueValues = new Map<IItemData, IItemData>();
      for (let index = 0; index < arr.length; ++index) {
        const item = arr[index];

        // Do not return null values if existant (#14421)
        if (item == null) {
          continue;
        }

        const val = this.getValue(item);

        // TODO: comparator
        !uniqueValues.has(val) && uniqueValues.set(val, item);
      }
      return Array.from(uniqueValues.values());
    },

    selectAllItems(items: IItemData[]) {
      this.$nextTick(() => {
        if (this.selectedAllItems) {
          this.internalValue = [];
        } else {
          this.internalValue = items;
        }
      });
      if (this.clearOnSearchOnSelect) {
        (this.$refs.autocomplete as any).internalSearch = null;
      }
      if (this.clearOnSelect) {
        (this.$refs.autocomplete as any).clearableCallback();
      }
    },

    csiItemClass(item: IItem) {
      if (item.is_obsolete) {
        return 'csi-item--disabled';
      }
    }
  }
});
